import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { MenuItem, MessageService, SelectItem } from "primeng/api";
import { SessionStorageService } from "../../../services/session-storage.service";
import { catchError, debounceTime, finalize, forkJoin, of, Subject, switchMap, tap } from "rxjs";
import { Customer } from "../../../customer/customer.model";
import { CustomerService } from "../../../customer/customer.service";
import { Company } from "../../../company/company.model";
import { ContractsService } from "../../../contracts/contracts.service";
import { AccountsService } from "../../accounts.service";
import { CreditNote } from "../credit-note.model";
import { CreditNoteLine } from "../credit-note-line.model";
import { Invoice } from '../../invoice/invoice.model';
import { Bill } from "../../payables/generated-bills/bill.model";
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { PartyService } from '../../../party/party.service';
import { CrmClient, CrmClientCommunication } from '../../../crm/crm.model';
import { CrmClientContactPickerComponent } from '../../../shared/crm-client-contact-picker/crm-client-contact-picker.component';
import { CRM_PAYABLE_ROLES, CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { AccountingTaxCode } from '../../accounting-tax-code.model';

@Component({
    selector: 'app-add-edit-credit-note',
    templateUrl: './add-edit-credit-note.component.html',
    styleUrls: ['./add-edit-credit-note.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BreadcrumbModule,
        SelectModule,
        AutoCompleteModule,
        ButtonModule,
        TooltipModule,
        AutoCompleteModule,
        InputNumberModule,
        InputTextModule,
        TextareaModule,
        DatePickerModule,
        SelectModule,
        ToastModule,
        ProgressBarModule,
        DividerModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        CrmClientContactPickerComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageFormSectionComponent,
        PageDataPanelComponent
    ],
    standalone: true,
    providers: [
        MessageService,
        AccountsService,
        CustomerService,
        ContractsService,
        SessionStorageService,
        PartyService
    ]
})
export class AddEditCreditNoteComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};
    creditNote!: CreditNote;
    creditNoteForm!: FormGroup;
    editMode = false;
    creditNoteId!: number;
    isLoading: boolean = false;

    companies: SelectItem<Company>[] = [];
    customers: SelectItem<Customer>[] = [];
    filteredBills: Bill[] = [];
    filteredInvoices: Invoice[] = [];
    crmClients: CrmClient[] = [];
    crmContacts: CrmClientCommunication[] = [];
    selectedCrmClient?: CrmClient | null;
    selectedCrmContact?: CrmClientCommunication | null;
    private billSearchTerms = new Subject<string>();
    private invoiceSearchTerms = new Subject<string>();

    canSave = false;
    lineAccounts: SelectItem<number>[] = [];
    taxCodes: AccountingTaxCode[] = [];
    vatEligibility = [{ label: 'Recoverable', value: true }, { label: 'Not recoverable', value: false }];

    subtotal: number = 0;
    tax: number = 0;
    total: number = 0;
    currencyCode: string = 'SAR'; // Default currency

    get pageTitle(): string {
        return this.editMode ? 'Edit Credit Note' : 'New Credit Note';
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Subtotal', value: this.formatMoney(this.subtotal), hint: 'Lines before tax', tone: 'info' },
            { label: 'Estimated tax', value: this.formatMoney(this.tax), hint: 'Calculated from selected tax codes' },
            { label: 'Estimated total', value: this.formatMoney(this.total), hint: 'Includes estimated tax', tone: 'success' },
            { label: 'Lines', value: this.getCreditNoteLineControls?.length || 0, hint: 'Adjustment lines' }
        ];
    }

    get selectedVendorName(): string {
        return this.selectedCrmClient?.companyName
            || this.creditNoteForm?.get('customer')?.value?.name
            || 'Vendor not selected';
    }

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private location: Location,
        private messageService: MessageService,
        private creditNoteService: AccountsService,
        private customerService: CustomerService,
        private contractService: ContractsService,
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        private permissionService: PermissionService,
        private chartAccounts: ChartOfAccountsService,
        private partyService: PartyService,
        private crmPartyContext: CrmPartyContextService
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Credit Note', routerLink: '/accounts/enterprise/payables/credit-note' },
            { label: this.editMode ? 'Edit Credit Note' : 'Add Credit Note' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.initForm();
        this.loadAccountingOptions();
        this.initFilterLogic();
        this.route.params.subscribe(params => {
            this.creditNoteId = params['id'];
            this.editMode = !!this.creditNoteId;
            this.canSave = this.permissionService.hasPermission(this.editMode
                ? app_permissions.accounts.credit_note.edit : app_permissions.accounts.credit_note.create);
            this.loadData();
        });


    }

    loadData() {
        this.isLoading = true;

        const loadObservables = {
            companies: this.loadCompanies(),
            crmClients: this.crmPartyContext.loadClients(this.sessionStorageService.getCompany()?.id, CRM_PAYABLE_ROLES),
        };

        forkJoin(loadObservables)
            .pipe(
                tap(data => {
                    this.crmClients = data.crmClients ?? [];
                    if (this.editMode) {
                        this.loadCreditNote().subscribe();
                    } else {
                        if (this.companies.length > 0) {
                            this.onCompanyChange(this.companies[0].value);
                            this.creditNoteForm.patchValue({ company: this.companies[0].value });
                        }
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'An error occurred while loading data.' + error.message
                    });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                    if (!this.editMode) this.restoreOriginalBillFromRoute();
                }
            });
    }

    private restoreOriginalBillFromRoute(): void {
        const id = Number(this.route.snapshot.queryParamMap.get('originalBillId'));
        if (!id || this.editMode) return;
        this.isLoading = true;
        this.accountsService.getBillById(id).pipe(finalize(() => this.isLoading = false)).subscribe({
            next: bill => {
                if (!bill?.billId || bill.owningCompany?.id !== this.sessionStorageService.getCompany()?.id) {
                    this.messageService.add({ severity: 'error', summary: 'Original bill unavailable', detail: 'Choose a bill belonging to the selected company.' });
                    return;
                }
                this.creditNoteForm.patchValue({ bill });
                this.onBillSelected(bill);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Original bill unavailable', detail: 'Could not load the bill for this adjustment note.' })
        });
    }

    loadCompanies() {
        return this.partyService.getParties(this.sessionStorageService.getCompany()?.id ?? 0, ['Company'], '', 0, 1000).pipe(
            tap(value => {
                this.companies = value.content.map(party => this.partyService.convertPartyToCompany(party)).map(company => ({
                    value: company,
                    label: company.name
                }));
            })
        );
    }

    onCompanyChange(company: Company) {
        if (company) {
            this.loadCustomersByCompany(company).subscribe();
            //this.billSearchTerms.next('');
            //this.invoiceSearchTerms.next('');
        } else {
            this.customers = [];
            this.creditNoteForm.patchValue({ customer: null });
            this.onCrmClientPicked(null);
            this.filteredBills = [];
            this.filteredInvoices = [];
            this.creditNoteForm.patchValue({ bill: null, invoice: null });
            this.currencyCode = 'SAR'; // Reset to default
        }
    }

    loadCustomersByCompany(company: Company) {
        return this.customerService.getCustomersByCompany(company.id).pipe(
            tap(customers => {
                this.customers = customers.map(customer => ({
                    value: customer,
                    label: customer.name
                }));
            })
        );
    }

    loadCreditNote() {
        return this.creditNoteService.getCreditNoteById(this.creditNoteId).pipe(
            tap(creditNote => {
                this.creditNote = creditNote;
                if (!isEditableFinancialDraft(creditNote, this.sessionStorageService.getCompany()?.id)) {
                    this.canSave = false;
                    this.creditNoteForm.disable();
                    this.messageService.add({ severity: 'error', summary: 'Draft unavailable', detail: 'Only unposted Draft notes for the selected company can be edited.' });
                    return;
                }
                this.creditNoteForm.patchValue(creditNote);
                this.creditNoteForm.get('reason')?.setValue(creditNote.reason || creditNote.manualReasonText || '');
                this.onCompanyChange(creditNote.company!);
                this.restoreCrmSelection(creditNote);
                this.creditNoteForm.setControl('creditNoteLines', this.fb.array(creditNote.creditNoteLines!
                    .map(line => this.createCreditNoteLineFormGroup(line))));
            })
        );
    }

    initForm() {
        this.creditNoteForm = this.fb.group({
            creditNoteID: [null],
            creditNoteNumber: [''],
            company: [null],
            customer: [null],
            crmClientId: [null],
            crmClientContactId: [null],
            bill: [null],
            invoice: [null],
            creditNoteDate: [new Date(), Validators.required],
            reason: [''],
            supplierReference: [''],
            currencyCode: ['SAR', Validators.required],
            createdAt: [null],
            createdBy: [null],
            updatedAt: [null],
            updatedBy: [null],
            creditNoteLines: this.fb.array([this.createCreditNoteLineFormGroup()]),
        });

        this.creditNoteForm.valueChanges.subscribe(() => {
            this.calculateTotals();
        });
        this.creditNoteForm.get('currencyCode')?.valueChanges.subscribe(value => {
            const changed = (value || 'SAR').toUpperCase() !== this.currencyCode.toUpperCase();
            this.currencyCode = value || 'SAR';
            if (changed) this.getCreditNoteLineControls.controls.forEach(line => this.clearTaxSnapshot(line as FormGroup));
            this.calculateTotals();
        });
        this.calculateTotals();
    }

    createCreditNoteLineFormGroup(creditNoteLine?: CreditNoteLine): FormGroup {
        const group = this.fb.group({
            creditNoteLineID: [creditNoteLine?.creditNoteLineID ?? null],
            description: [creditNoteLine?.description ?? ''],
            amount: [creditNoteLine?.amount ?? 0, [Validators.required, Validators.min(0)]],
            accountId: [creditNoteLine?.accountId ?? null],
            taxCode: [creditNoteLine?.taxCode ?? null],
            inputVatRecoverable: [creditNoteLine?.inputVatRecoverable ?? null],
            taxRate: [creditNoteLine?.taxRate ?? null], taxCategory: [creditNoteLine?.taxCategory ?? null],
            taxableAmount: [creditNoteLine?.taxableAmount ?? null], exemptAmount: [creditNoteLine?.exemptAmount ?? null],
            zeroRatedAmount: [creditNoteLine?.zeroRatedAmount ?? null], taxAmount: [creditNoteLine?.taxAmount ?? null],
            totalAmount: [creditNoteLine?.totalAmount ?? null], exemptionReason: [creditNoteLine?.exemptionReason ?? null],
        });
        group.get('amount')?.valueChanges.subscribe(() => this.clearTaxSnapshot(group));
        group.get('taxCode')?.valueChanges.subscribe(() => {
            this.clearTaxSnapshot(group);
            group.patchValue({ exemptionReason: null }, { emitEvent: false });
        });
        return group;
    }

    private clearTaxSnapshot(group: FormGroup): void {
        group.patchValue({ taxRate: null, taxCategory: null, taxableAmount: null, exemptAmount: null,
            zeroRatedAmount: null, taxAmount: null, totalAmount: null }, { emitEvent: false });
    }

    get getCreditNoteLineControls(): FormArray {
        return this.creditNoteForm.get('creditNoteLines') as FormArray;
    }

    addCreditNoteLine() {
        this.getCreditNoteLineControls.push(this.createCreditNoteLineFormGroup());
    }

    removeCreditNoteLine(index: number) {
        this.getCreditNoteLineControls.removeAt(index);
    }

    saveCreditNote() {
        if (!this.canSave || (this.editMode && !isEditableFinancialDraft(this.creditNote, this.sessionStorageService.getCompany()?.id))) return;
        if (this.creditNoteForm.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct all form errors.'
            });
            return;
        }
        this.isLoading = true;
        const modifiedCreditNote = { ...(this.editMode ? this.creditNote : {}), ...this.creditNoteForm.getRawValue() } as CreditNote;
        modifiedCreditNote.crmClientId = this.selectedCrmClient?.id;
        modifiedCreditNote.crmClientContactId = this.selectedCrmContact?.id;
        modifiedCreditNote.owningCompanyId = this.sessionStorageService.getCompany()!.id;
        const userId = this.sessionStorageService.getUser()!.id

        if (this.editMode) {
            modifiedCreditNote.createdAt = this.creditNote.createdAt;
            modifiedCreditNote.createdBy = this.creditNote.createdBy;
            modifiedCreditNote.updatedAt = new Date();
            modifiedCreditNote.updatedBy = userId;
        } else {
            modifiedCreditNote.createdAt = new Date();
            modifiedCreditNote.createdBy = userId;
            modifiedCreditNote.updatedAt = new Date();
            modifiedCreditNote.updatedBy = userId;
        }
        modifiedCreditNote.creditNoteLines?.forEach(line => {
            line.creditNoteID = modifiedCreditNote.creditNoteID;
            line.updatedBy = userId;
        })
        this.creditNoteService.createCreditNote(modifiedCreditNote).subscribe({
            next: (newCreditNote: CreditNote) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Credit Note# ' + newCreditNote.creditNoteNumber + ' saved successfully.'
                });
                setTimeout(() => {
                    this.router.navigate(['/accounts/enterprise/payables/credit-note']);
                }, 3000);
            },
            error: (error) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error?.error?.message || 'Error saving credit note'
                });
            }
        });
    }

    filterBills(event: any) {
        this.billSearchTerms.next(event.query);
    }

    filterInvoices(event: any) {
        this.invoiceSearchTerms.next(event.query);
    }

    onCrmClientPicked(client: CrmClient | null): void {
        this.selectedCrmClient = client;
        this.selectedCrmContact = null;
        this.crmContacts = [];
        this.creditNoteForm.patchValue({ crmClientId: client?.id ?? null, crmClientContactId: null });
        this.patchLegacyPartyFromClient(client);
        if (client?.id) {
            this.crmPartyContext.loadContacts(client.id, this.sessionStorageService.getCompany()?.id).subscribe(contacts => {
                this.crmContacts = contacts;
                this.selectedCrmContact = this.crmPartyContext.preferredContact(contacts);
                this.creditNoteForm.patchValue({ crmClientContactId: this.selectedCrmContact?.id ?? null });
            });
        }
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.selectedCrmContact = contact;
        this.creditNoteForm.patchValue({ crmClientContactId: contact?.id ?? null });
    }

    onBillSelected(bill: Bill): void {
        if (!bill) return;
        if (bill.country?.currencyCode) this.creditNoteForm.patchValue({ currencyCode: bill.country.currencyCode });
        this.creditNoteForm.patchValue({
            originalBillId: bill.billId,
            originalBillNumber: bill.billNumber
        });
        this.restoreCrmSelection(bill as any);
    }

    private restoreCrmSelection(record: { crmClientId?: number; crmClientContactId?: number; crmClient?: CrmClient | null }): void {
        const client = this.crmClients.find(item => item.id === record.crmClientId)
            || record.crmClient
            || null;
        if (!client) return;
        this.selectedCrmClient = client;
        this.creditNoteForm.patchValue({ crmClientId: client.id ?? null });
        this.patchLegacyPartyFromClient(client);
        this.crmPartyContext.loadContacts(client.id, this.sessionStorageService.getCompany()?.id).subscribe(contacts => {
            this.crmContacts = contacts;
            this.selectedCrmContact = this.crmPartyContext.preferredContact(contacts, record.crmClientContactId);
            this.creditNoteForm.patchValue({ crmClientContactId: this.selectedCrmContact?.id ?? null });
        });
    }

    private patchLegacyPartyFromClient(client?: CrmClient | null): void {
        if (!client) return;
        const customer = client.linkedCustomerId
            ? this.customers.find(option => option.value?.id === client.linkedCustomerId)?.value
            : null;
        const company = client.linkedCompanyId
            ? this.companies.find(option => option.value?.id === client.linkedCompanyId)?.value
            : null;
        this.creditNoteForm.patchValue({
            customer: customer ?? this.creditNoteForm.get('customer')?.value,
            company: company ?? this.creditNoteForm.get('company')?.value
        });
    }

    calculateTotals(): void {
        this.subtotal = this.getCreditNoteLineControls.controls.reduce((acc, line) => {
            const amount = line.get('amount')?.value || 0;
            return acc + amount;
        }, 0);
        this.tax = this.getCreditNoteLineControls.controls.reduce((sum, line) => {
            const code = this.taxCodes.find(item => item.taxCode === line.get('taxCode')?.value);
            return sum + (line.get('taxAmount')?.value ?? Number(line.get('amount')?.value || 0) * Number(code?.taxRate || 0) / 100);
        }, 0);
        this.total = this.subtotal + this.tax;
    }

    private loadAccountingOptions(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        forkJoin({ accounts: this.chartAccounts.getAllChartOfAccountsByCompany(companyId, false),
            taxCodes: this.accountsService.getAccountingTaxCodes(companyId) }).subscribe({
            next: result => {
                this.lineAccounts = result.accounts.filter(account => account.isActive && !account.deprecated
                    && ['expense', 'asset'].includes((account.internalType || '').toLowerCase()))
                    .map(account => ({ label: `${account.accountCode} - ${account.accountName}`, value: account.accountId }));
                this.taxCodes = result.taxCodes;
                this.calculateTotals();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Accounting options unavailable',
                detail: 'Could not load accounts and tax codes.' })
        });
    }

    formatMoney(amount: number | undefined): string {
        return `${this.currencyCode || 'SAR'} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }


    private initFilterLogic() {
        this.billSearchTerms
            .pipe(
                debounceTime(300),
                switchMap((searchText: string) => {
                    const clientId = this.selectedCrmClient?.id;
                    const owningCompanyId = this.sessionStorageService.getCompany()!!.id;
                    return this.accountsService.getGeneratedBills(0, 20, owningCompanyId,
                        clientId ? [clientId] : undefined, undefined, undefined, undefined, undefined,
                        undefined, undefined, searchText).pipe(catchError(() => {
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not search bills.' });
                            return of({ items: [] });
                        }));
                }),
                tap(res => this.filteredBills = res.items)
            )
            .subscribe();

        this.invoiceSearchTerms
            .pipe(
                debounceTime(300),
                switchMap((searchText: string) => {
                    const clientId = this.selectedCrmClient?.id;
                    const owningCompanyId = this.sessionStorageService.getCompany()!!.id;
                    return this.accountsService.getGeneratedInvoices(0, 20, owningCompanyId,
                        clientId ? [clientId] : undefined, [], undefined, undefined, searchText).pipe(catchError(() => {
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not search invoices.' });
                            return of({ items: [] });
                        }));
                }),
                tap(res => this.filteredInvoices = res.items)
            )
            .subscribe();
    }

    cancel() {
        this.location.back();
    }
}
